import React, { useCallback, useState } from 'react';
import { NextPageContext } from 'next';

import {
  Accordion,
  Amenities,
  Breadcrumbs,
  Button,
  ButtonBlock,
  Calculator,
  CtaButtons,
  EmailButton,
  FAQs,
  Floorplans,
  HeroLocation,
  LocationCard,
  LocationsInSection,
  PhotoModal,
  ServicesGlobals,
  TourTimes,
} from '../../../../components';
import { ApplyButtons } from '../../../../components/forms/NursingHomeForm/Fields';
import Image from '../../../../components/layout/Image';
import { useSiteQuery, useStore } from '../../../../hooks';
import { getLocationTitle } from '../../../../lib/FunctionLib';
import ErrorPage from '../../../_error';
import NotFoundPage from '../../../404';

import query from './index.graphql';

import styles from './index.module.scss';

interface Props {
  slug: string;
}

const NursingHomesLocationsPage = ({ slug }: Props) => {
  const globals = useStore('globals');
  const [{ data, error }] = useSiteQuery<HomeLocationsDetailQuery>({ query, variables: { slug } });
  const [photoModalOpen, openPhotoModal] = useState(false);
  const phone = globals.phone || null;

  const togglePhotoModal = useCallback(() => {
    const toSet = !photoModalOpen;
    openPhotoModal(toSet);
  }, [photoModalOpen]);

  // function onGalleryClick(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) {
  //   if (e.type !== 'keypress' || (e as React.KeyboardEvent).key === 'Enter') {
  //     togglePhotoModal();
  //   }
  // }

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  const entry = data?.entry;
  if (entry) {
    const amenities = entry.amenities || [];
    const headings = [
      { title: 'Home Overview', slug: 'home-overview' },
      { title: amenities.length > 0 ? 'Location & Amenities' : 'Location', slug: 'location' },
      { title: 'Services provided', slug: 'additional-services' },
      { title: 'Fees & Pricing', slug: 'fees-pricing' },
      { title: 'Pricing Calculator', slug: 'pricing-calculator' },
      { title: 'Other locations', slug: 'other-locations' },
    ];

    const title = getLocationTitle(entry);
    const photos = entry.photos?.filter((p): p is AssetFragment => p !== null && p !== undefined);
    const floorplans = entry.floorplans || [];
    const image = entry.image?.[0];
    const applyBtn = entry.feesButtons?.[0];

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Residential Aged Care Homes', uri: '/nursing-homes' },
      { title: 'Residential Aged Care Homes Locations', uri: '/nursing-homes/locations' },
      { title, uri: `/${entry.uri!}` },
    ];

    return (
      <>
        <Breadcrumbs breadcrumbs={breadcrumbs} additionalClasses={'override-bg-teal'} />
        <HeroLocation
          sectionHeading="Residential Aged Care Homes Locations"
          title={title}
          headline={entry.headline!}
          headings={headings}
          photos={photos}
          buttons={entry?.feesButtons}
          additionalClasses={'override-bg-teal-signature'}
        />
        <LocationsInSection customTitle="Explore our other homes" current={entry} all={data?.allLocations} />
        <div className="section left">
          <div className="container">
            <div className="row">
              {floorplans.length === 0 && image && (
                <div className="col-lg-6">
                  <div className={styles.overviewImage}>
                    <Image {...image} alt={title} />
                  </div>
                </div>
              )}
              <div className="col-lg-6">
                <div id="home-overview" className="sectionHeading">
                  Home Overview
                </div>
                <div dangerouslySetInnerHTML={{ __html: entry.body! }} />
                <div className="desktopOnly">
                  <p>
                    <strong>
                      Get in touch for more details
                      {phone && (
                        <>
                          {' or call us on '}
                          <a href={`tel:${phone?.replace(/[^\d\+]/g, '')}`}>{phone}</a>
                        </>
                      )}
                    </strong>
                  </p>
                  {applyBtn && (floorplans.length > 0 || image) && (
                    <ApplyButtons
                      button={applyBtn}
                      customEmailDesktop={
                        <EmailButton section="Nursing Home Location" name={entry.title!} type="orangeBorder" />
                      }
                      desktop
                    />
                  )}
                  {entry.tourTimes && <TourTimes time={entry.tourTimes} className={styles.tourTimes} />}
                </div>
              </div>
              {(floorplans.length > 0 || image) && (
                <div className="col-lg-6">
                  {floorplans.length > 0 && <Floorplans floorplans={floorplans} />}
                  <div className="mobileOnly">
                    <p>
                      <strong>
                        Get in touch for more details
                        {phone && (
                          <>
                            {' or call us on '}
                            <a href={`tel:${phone?.replace(/[^\d\+]/g, '')}`}>{phone}</a>
                          </>
                        )}
                      </strong>
                    </p>
                    {entry.tourTimes && <TourTimes time={entry.tourTimes} className={styles.tourTimes} />}
                    {applyBtn && (
                      <ApplyButtons
                        button={applyBtn}
                        customEmailMobile={
                          <EmailButton section="Nursing Home Location" name={entry.title!} text="Email us" />
                        }
                        mobile
                      />
                    )}
                  </div>
                </div>
              )}
              {floorplans.length === 0 && !image && (
                <div className="col-lg-6">
                  <div className="mobileOnly">
                    {entry.tourTimes && <TourTimes time={entry.tourTimes} className={styles.tourTimes} />}
                  </div>
                  <CtaButtons
                    headline="Get in touch for more details about this property"
                    description="Send an email or give us a call during business hours"
                  >
                    {applyBtn && (
                      <div>
                        <ButtonBlock item={{ ...applyBtn, includeArrow: false, buttonStyle: 'orangeBorder' }} />
                      </div>
                    )}
                    <div>
                      <EmailButton type="orangeBorder" section="Nursing Home Location" name={entry.title!} />
                    </div>
                  </CtaButtons>
                </div>
              )}
            </div>
          </div>
        </div>
        <Amenities title={entry.title!} address={entry.address!} amenities={entry.amenities} />
        <ServicesGlobals isMain customTitle="Services provided" />
        <div className="section section-white">
          <div className="container">
            <div id="fees-pricing" className="sectionHeading">
              Fees & Pricing
            </div>
            <div className="row">
              <div className="col-lg-6">
                <div dangerouslySetInnerHTML={{ __html: entry.feesPricing! }} />
              </div>
              <div className="col-lg-6">
                <div className={styles.accordionBlock}>
                  {entry.feesPricingAccordion?.map(
                    (item) =>
                      item && (
                        <Accordion key={item!.title!} title={item!.title!} blockStyle>
                          <div dangerouslySetInnerHTML={{ __html: item!.text! }} />
                        </Accordion>
                      ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="section left pb-2">
          <div className="container">
            <div id="pricing-calculator" className="sectionHeading">
              Pricing Calculator
            </div>
            {!!entry.rooms?.length && (
              <div className="row">
                <div className="col-lg-6">
                  <div dangerouslySetInnerHTML={{ __html: entry.pricingCalculator! }} />
                  {entry.feesButtons?.map((button) => button && <ButtonBlock key={button.uid!} item={button} />)}
                </div>
                <div className="col-lg-6">
                  <Calculator rooms={entry.rooms} />
                </div>
              </div>
            )}
            {!entry.rooms?.length && <div dangerouslySetInnerHTML={{ __html: entry.pricingCalculator! }} />}
          </div>
        </div>
        <div className="section">
          <div className="container">
            <h2 id="other-locations" className="center">
              Other Aged Care Home locations
            </h2>
            <div className={`${styles.cardGrid} cardGrid`}>
              {data?.allLocations
                ?.filter((item) => item?.slug !== entry.slug!)
                .slice(0, 3)
                .map((item) => item && <LocationCard key={item.uid!} item={item} />)}
            </div>
            <div className={styles.all}>
              <Button href="/nursing-homes/locations" type="link" includeArrow>
                See all locations
              </Button>
            </div>
          </div>
        </div>
        <FAQs topic="nursing-homes" />
        {photoModalOpen && photos && <PhotoModal photos={photos} handleClose={togglePhotoModal} />}
      </>
    );
  }
  return <NotFoundPage />;
};

NursingHomesLocationsPage.getInitialProps = ({ req }: NextPageContext) => ({
  slug: req?.url?.substring(req?.url?.lastIndexOf('/') + 1),
});

export default NursingHomesLocationsPage;
